import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import api, { formatApiError } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';

// Inline approval modal for any prepared email draft. The parent component is
// expected to pass a draft object (from POST /applications/:id/emails/prepare
// or PATCH /status) and handle what happens after send/skip via `onClose`.
//
// Closing the dialog without pressing Send or Skip is treated as a skip - that
// way we always have an audit trail entry for every prepared draft.
export default function EmailApprovalDialog({ draft, open, onClose }) {
  const { t } = useLanguage();
  const [working, setWorking] = useState(false);
  const [form, setForm] = useState({
    subject: '',
    body_text: '',
    body_html: '',
    language: 'fr',
    recipient_email: '',
    recipient_name: '',
  });
  const [dirty, setDirty] = useState(false);
  const decidedRef = useRef(false); // prevents close-as-skip after send/skip
  const draftIdRef = useRef(null);

  useEffect(() => {
    if (draft && draft.id) {
      decidedRef.current = false;
      draftIdRef.current = draft.id;
      setDirty(false);
      setForm({
        subject: draft.subject || '',
        body_text: draft.body_text || '',
        body_html: draft.body_html || '',
        language: draft.language || 'fr',
        recipient_email: draft.recipient_email || '',
        recipient_name: draft.recipient_name || '',
      });
    }
  }, [draft]);

  const persistEdits = async () => {
    if (!dirty || !draftIdRef.current) return true;
    try {
      // We intentionally do NOT send body_html here. The backend regenerates
      // the HTML body from body_text (wrapped in the Nest Geneva shell) so
      // admin edits in the textarea flow through without us stripping the
      // template styling on the frontend.
      await api.patch(`/emails/${draftIdRef.current}`, {
        subject: form.subject,
        body_text: form.body_text,
        recipient_email: form.recipient_email,
        recipient_name: form.recipient_name,
      });
      setDirty(false);
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save draft edits');
      return false;
    }
  };

  const handleLanguageFlip = async (next) => {
    if (!draftIdRef.current || next === form.language) return;
    // Guard against silent data loss: if the admin has unsaved edits in the
    // body/subject/recipient, flipping language would re-render from the
    // template and clobber those edits. Confirm before doing so.
    if (dirty) {
      const ok = window.confirm(
        'Switching language will discard the edits you just made. Continue?'
      );
      if (!ok) return;
    }
    setWorking(true);
    try {
      const res = await api.patch(`/emails/${draftIdRef.current}`, { language: next });
      const d = res.data || {};
      setForm((prev) => ({
        ...prev,
        language: d.language || next,
        subject: d.subject ?? prev.subject,
        body_text: d.body_text ?? prev.body_text,
        body_html: d.body_html ?? prev.body_html,
      }));
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not switch language');
    } finally {
      setWorking(false);
    }
  };

  const handleSend = async () => {
    if (!draftIdRef.current) return;
    setWorking(true);
    try {
      const saved = await persistEdits();
      if (!saved) return;
      const res = await api.post(`/emails/${draftIdRef.current}/send`);
      decidedRef.current = true;
      const to = res?.data?.recipient_email || form.recipient_email;
      toast.success(
        to ? `${t('admin.email.sent')}: ${to}` : t('admin.email.sent')
      );
      onClose?.({ decision: 'sent' });
    } catch (err) {
      const status = err?.response?.status;
      const rawDetail = err?.response?.data?.detail;
      const detail = rawDetail != null ? formatApiError(rawDetail) : '';
      const code = err?.code;
      const isTimeoutLike =
        status === 504 ||
        status === 502 ||
        status === 503 ||
        code === 'ECONNABORTED' ||
        code === 'ETIMEDOUT' ||
        code === 'ERR_NETWORK';
      if (isTimeoutLike) {
        toast.error(detail || t('admin.email.timeout'));
      } else {
        toast.error(detail || t('admin.email.failed'));
      }
    } finally {
      setWorking(false);
    }
  };

  const handleSkip = async (reason = 'dismissed') => {
    if (!draftIdRef.current || decidedRef.current) {
      onClose?.({ decision: 'skipped' });
      return;
    }
    // Closing the dialog used to silent-skip. Confirm so admins don't think
    // "Propose listing" already emailed the tenant.
    if (reason === 'dismissed') {
      const ok = window.confirm(t('admin.email.confirmSkip'));
      if (!ok) return;
    }
    setWorking(true);
    try {
      await api.post(`/emails/${draftIdRef.current}/skip`, { reason });
      decidedRef.current = true;
      toast.success(
        reason === 'dismissed' ? t('admin.email.skippedNoSend') : t('admin.email.skipped')
      );
      onClose?.({ decision: 'skipped' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not skip draft');
    } finally {
      setWorking(false);
    }
  };

  const handleOpenChange = (next) => {
    if (working) return;
    if (!next) {
      // close === skip (dismissed)
      handleSkip('dismissed');
    }
  };

  if (!draft) return null;

  const recipientLabel = form.recipient_name
    ? `${form.recipient_name} <${form.recipient_email}>`
    : form.recipient_email;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="email-approval-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.email.reviewTitle')}</DialogTitle>
          <p className="text-xs text-[#5a5a5a]">{t('admin.email.reviewSubtitle')}</p>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {t('admin.email.recipient')}: {recipientLabel || '-'}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {draft.event_key}
            </Badge>
            <div className="ml-auto flex items-center gap-1">
              <span className="text-xs text-[#5a5a5a] mr-1">{t('admin.email.language')}:</span>
              <Button
                size="sm"
                variant={form.language === 'fr' ? 'default' : 'outline'}
                onClick={() => handleLanguageFlip('fr')}
                disabled={working}
                type="button"
              >
                FR
              </Button>
              <Button
                size="sm"
                variant={form.language === 'en' ? 'default' : 'outline'}
                onClick={() => handleLanguageFlip('en')}
                disabled={working}
                type="button"
              >
                EN
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor="email-recipient" className="text-xs text-[#5a5a5a]">
              {t('admin.email.recipient')}
            </Label>
            <Input
              id="email-recipient"
              value={form.recipient_email}
              onChange={(e) => { setForm({ ...form, recipient_email: e.target.value }); setDirty(true); }}
              disabled={working}
            />
          </div>

          <div>
            <Label htmlFor="email-subject" className="text-xs text-[#5a5a5a]">
              {t('admin.email.subject')}
            </Label>
            <Input
              id="email-subject"
              value={form.subject}
              onChange={(e) => { setForm({ ...form, subject: e.target.value }); setDirty(true); }}
              disabled={working}
            />
          </div>

          <div>
            <Label htmlFor="email-body" className="text-xs text-[#5a5a5a]">
              {t('admin.email.body')}
            </Label>
            <Textarea
              id="email-body"
              value={form.body_text}
              onChange={(e) => {
                setForm({ ...form, body_text: e.target.value });
                setDirty(true);
              }}
              disabled={working}
              rows={12}
              className="font-mono text-sm"
            />
          </div>
        </div>

        <DialogFooter className="mt-2 flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleSkip('manual')}
            disabled={working}
            type="button"
          >
            {t('admin.email.skip')}
          </Button>
          <Button
            onClick={handleSend}
            disabled={working || !form.recipient_email || !form.subject}
            type="button"
            data-testid="email-approval-send"
          >
            {working ? t('admin.email.sending') : t('admin.email.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
